import { forwardRef, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Load the pdf.js worker from a CDN matching the installed pdfjs-dist version.
// Must be set in this same module (see react-pdf docs) — not a shared setup file.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 1.75];
// Only mount an actual PDF canvas for pages within this many positions of
// the page currently being viewed — rendering every page's canvas up front
// on a 100+ page book would lock up the browser.
const RENDER_WINDOW = 3;
const BOOKMARK_STORAGE_PREFIX = "book-viewer:last-page:";
// Matches the .book-closing keyframe duration in index.css — the flipbook
// stays mounted (with the closing animation class applied) for this long
// before we actually swap back to the closed single-cover view.
const CLOSE_ANIMATION_MS = 420;

const PageFace = forwardRef(function PageFace(
  { pageNumber, width, height, shouldRender, onMeasured },
  ref
) {
  return (
    <div ref={ref} style={{ width, height }} className="book-paper overflow-hidden">
      {shouldRender ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          loading={null}
          renderAnnotationLayer={false}
          onLoadSuccess={(page) => onMeasured(page.height / page.width)}
        />
      ) : null}
    </div>
  );
});

export default function BookPageViewer({ pdfUrl }) {
  const [numPages, setNumPages] = useState(null);
  // Closed = just the cover shown by itself, no book chrome. Only once the
  // reader actually turns past it do we mount the two-page flipbook —
  // matches how a real closed book looks before you open it.
  const [opened, setOpened] = useState(false);
  // True only for the brief window while the close animation plays — the
  // flipbook stays mounted (with .book-closing applied) until it finishes.
  const [closing, setClosing] = useState(false);
  // 0-indexed position *within the opened flipbook*, whose pages start at
  // real PDF page 2 (the cover, page 1, is handled by the closed view).
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(1);
  const [availableWidth, setAvailableWidth] = useState(900);
  // Measured from the actual PDF once a page renders — used instead of a
  // guessed aspect ratio so the reserved layout box always matches what's
  // really drawn (see PageFace.onMeasured).
  const [heightPerWidth, setHeightPerWidth] = useState(1.414);
  const measuredRef = useRef(false);
  const containerRef = useRef(null);
  const bookWrapperRef = useRef(null);
  const flipBookRef = useRef(null);

  // Remembers where the reader left off (per book) so reopening can jump
  // straight back there instead of stepping through page by page.
  const storageKey = BOOKMARK_STORAGE_PREFIX + pdfUrl;
  const [bookmarkedPage, setBookmarkedPage] = useState(() => {
    const saved = window.localStorage.getItem(storageKey);
    return saved ? Number(saved) : null;
  });
  // Tracks the latest page reached while open, without itself being state —
  // read from when the book is closed to update the bookmark.
  const lastOpenPageRef = useRef(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      setAvailableWidth(entries[0]?.contentRect.width ?? 900);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const zoom = ZOOM_STEPS[zoomIndex];
  // Below the sm breakpoint: one full-width page at a time, arrows moved
  // down next to the toolbar (see render below), and no two-page spread —
  // matches how the reference site behaves on phones. At sm and up: a real
  // two-page spread, arrows flanking the book.
  const isNarrow = availableWidth < 640;
  const useSpread = opened && !isNarrow;

  const baseSingleWidth = Math.max(140, Math.min(420, availableWidth - 32));
  const baseSpreadHalfWidth = Math.max(
    160,
    Math.min(380, (availableWidth - 120) / 2)
  );
  const basePageWidth = useSpread ? baseSpreadHalfWidth : baseSingleWidth;
  const pageWidth = basePageWidth * zoom;
  const pageHeight = pageWidth * heightPerWidth;
  // react-pageflip's "fixed" size mode only reads width/height once, at
  // init — bucketing the width into a remount key means the book actually
  // resizes when the *screen* changes (window resize, phone rotation), not
  // just on a zoom click. Bucketing avoids remounting on every single pixel
  // during a drag-resize.
  const sizeBucket = Math.round(pageWidth / 8);

  // Pages shown inside the opened flipbook, starting from real page 2 —
  // the cover (page 1) is only ever shown in the closed view.
  const flipbookPageCount = numPages ? Math.max(0, numPages - 1) : 0;
  const displayedPage = opened ? 2 + spreadIndex : 1;
  const canGoNext = opened
    ? !!numPages && displayedPage < numPages
    : !!numPages && numPages > 1;
  const canGoPrev = opened;

  // Save progress as the reader flips through — only while actually open,
  // so closing the book (which shows "page 1" for the cover) never
  // overwrites how far they'd gotten.
  useEffect(() => {
    if (opened && numPages) {
      window.localStorage.setItem(storageKey, String(displayedPage));
      lastOpenPageRef.current = displayedPage;
    }
  }, [opened, displayedPage, numPages, storageKey]);

  function closeBook() {
    if (!opened || closing) return;
    // Play the closing animation first (see .book-closing in index.css),
    // then swap to the closed single-cover view once it finishes — matches
    // how a real book's cover swings shut before it settles closed.
    setClosing(true);
    window.setTimeout(() => {
      setBookmarkedPage(lastOpenPageRef.current);
      setOpened(false);
      setClosing(false);
    }, CLOSE_ANIMATION_MS);
  }

  function resumeFromBookmark() {
    if (!bookmarkedPage || bookmarkedPage < 2) return;
    setSpreadIndex(bookmarkedPage - 2);
    setOpened(true);
  }

  function handlePageMeasured(ratio) {
    if (measuredRef.current) return;
    measuredRef.current = true;
    setHeightPerWidth(ratio);
  }

  function goNext() {
    if (!opened) {
      setOpened(true);
      return;
    }
    flipBookRef.current?.pageFlip().flipNext();
  }

  function goPrev() {
    if (!opened) return;
    if (spreadIndex <= 0) {
      closeBook();
      return;
    }
    flipBookRef.current?.pageFlip().flipPrev();
  }

  function toggleFullscreen() {
    const el = bookWrapperRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.();
    }
  }

  return (
    <div ref={containerRef} className="flex w-full flex-col items-center">
      <div className="mb-4 rounded-full bg-zinc-800 px-4 py-1 text-sm text-white">
        page {displayedPage} of {numPages ?? "…"}
      </div>

      <div className="flex w-full max-w-full items-center justify-center gap-2 sm:gap-4">
        {/* On wider screens the arrows flank the book. Below sm, they move
            down next to the toolbar instead (see below) so the book itself
            gets the width back. */}
        <button
          type="button"
          aria-label="Previous page"
          onClick={goPrev}
          disabled={!canGoPrev}
          className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-400 text-white hover:bg-zinc-500 disabled:opacity-40 sm:flex"
        >
          ‹
        </button>

        <div ref={bookWrapperRef} className="flex flex-col items-center p-2">
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={
              <div
                style={{ width: pageWidth, height: pageHeight }}
                className="flex items-center justify-center text-zinc-500"
              >
                Loading book…
              </div>
            }
          >
            {numPages && !opened ? (
              // Closed book: just the cover, centered, no spread chrome.
              <div className="flex flex-col items-center">
                <div style={{ width: pageWidth, height: pageHeight }} className="rounded-sm border border-black/10 shadow-[0_15px_45px_rgba(0,0,0,0.3)]">
                  <Page
                    pageNumber={1}
                    width={pageWidth}
                    loading={null}
                    renderAnnotationLayer={false}
                    onLoadSuccess={(page) => handlePageMeasured(page.height / page.width)}
                  />
                </div>
                {bookmarkedPage && bookmarkedPage >= 2 && (
                  <button
                    type="button"
                    onClick={resumeFromBookmark}
                    className="mt-4 rounded-full bg-sky-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-sky-600"
                  >
                    Resume from page {bookmarkedPage} →
                  </button>
                )}
              </div>
            ) : null}

            {numPages && opened ? (
              // react-pageflip positions pages absolutely and doesn't
              // reserve layout height on its own — without an explicitly
              // sized, position:relative box around it, this collapses to 0
              // height and the toolbar below ends up rendered underneath
              // the book instead of below it.
              <div
                className={`relative${closing ? " book-closing" : ""}`}
                style={{
                  width: useSpread ? pageWidth * 2 : pageWidth,
                  height: pageHeight,
                }}
              >
                <HTMLFlipBook
                  // See sizeBucket above — remounts on zoom AND on real
                  // size changes. startPage keeps the remount landing back
                  // on the current page/spread.
                  key={`${zoomIndex}-${sizeBucket}-${useSpread}`}
                  ref={flipBookRef}
                  width={pageWidth}
                  height={pageHeight}
                  size="fixed"
                  minWidth={140}
                  maxWidth={600}
                  minHeight={200}
                  maxHeight={850}
                  showCover={false}
                  drawShadow
                  flippingTime={1400}
                  usePortrait={!useSpread}
                  startZIndex={0}
                  autoSize={false}
                  maxShadowOpacity={0.5}
                  mobileScrollSupport
                  clickEventForward
                  useMouseEvents
                  swipeDistance={30}
                  showPageCorners
                  disableFlipByClick={false}
                  startPage={spreadIndex}
                  className=""
                  style={{}}
                  onFlip={(event) => setSpreadIndex(event.data)}
                >
                  {Array.from({ length: flipbookPageCount }, (_, i) => (
                    <PageFace
                      key={i}
                      pageNumber={i + 2}
                      width={pageWidth}
                      height={pageHeight}
                      shouldRender={Math.abs(i - spreadIndex) <= RENDER_WINDOW}
                      onMeasured={handlePageMeasured}
                    />
                  ))}
                </HTMLFlipBook>

                {/* Center-spine shadow: react-pageflip's drawShadow only
                    animates a shadow during the flip itself — at rest,
                    two flat pages side by side don't read as an open book
                    without a static gutter shadow where they meet. */}
                {useSpread && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute top-0 h-full w-10 -translate-x-1/2"
                    style={{
                      left: "50%",
                      background:
                        "linear-gradient(to right, transparent, rgba(0,0,0,0.18) 50%, transparent)",
                    }}
                  />
                )}
              </div>
            ) : null}
          </Document>

          {/* Fullscreen / zoom toolbar. Below sm, prev/next flank it directly
              instead of sitting out on the sides of the whole book. */}
          <div className="mt-4 flex items-center gap-2 sm:gap-0">
            <button
              type="button"
              aria-label="Previous page"
              onClick={goPrev}
              disabled={!canGoPrev}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-400 text-white hover:bg-zinc-500 disabled:opacity-40 sm:hidden"
            >
              ‹
            </button>

            <div className="flex items-center gap-1 rounded-full bg-zinc-800/90 px-2 py-1.5 text-white">
              {opened && (
                <>
                  <ToolbarButton label="Close book" onClick={closeBook} disabled={closing}>
                    <CloseBookIcon />
                  </ToolbarButton>
                  <div className="h-5 w-px bg-white/20" />
                </>
              )}
              <ToolbarButton label="Fullscreen" onClick={toggleFullscreen}>
                <ExpandIcon />
              </ToolbarButton>
              <div className="h-5 w-px bg-white/20" />
              <ToolbarButton
                label="Zoom out"
                onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
                disabled={zoomIndex === 0}
              >
                <ZoomIcon out />
              </ToolbarButton>
              <ToolbarButton
                label="Zoom in"
                onClick={() =>
                  setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))
                }
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
              >
                <ZoomIcon />
              </ToolbarButton>
            </div>

            <button
              type="button"
              aria-label="Next page"
              onClick={goNext}
              disabled={!canGoNext}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-400 text-white hover:bg-zinc-500 disabled:opacity-40 sm:hidden"
            >
              ›
            </button>
          </div>
        </div>

        <button
          type="button"
          aria-label="Next page"
          onClick={goNext}
          disabled={!canGoNext}
          className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-400 text-white hover:bg-zinc-500 disabled:opacity-40 sm:flex"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function ToolbarButton({ label, onClick, disabled, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function ExpandIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseBookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v18H6.5A2.5 2.5 0 0 1 4 18.5v-13Z" strokeLinejoin="round" />
      <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H12v18h5.5a2.5 2.5 0 0 0 2.5-2.5v-13Z" strokeLinejoin="round" />
      <path d="M9 8l2 2-2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ZoomIcon({ out }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
      <path d="M8 11h6" strokeLinecap="round" />
      {!out && <path d="M11 8v6" strokeLinecap="round" />}
    </svg>
  );
}
